import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, noop } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  BaseButtonSize,
  baseCandidateActions,
  baseCandidatesSelectors,
  BaseCoreState,
  BaseModalHeaderSizes,
  BaseModalService,
  BaseTabInterface,
  BaseAbsenceTypeEnum,
  ProfileAbsenceListItemResponse,
  ProfileAbsencesTypeSummaryResponse,
  ProfileShowResponse,
  BaseAbsencePayloadTypeEnum,
  BaseAbsencePagination,
  MetaPagination,
} from 'base';
import { AbsenceTabsConst } from '../../../../_constants/absence-tabs.const';

export interface AbsencePagination {
  page: number;
  size: number;
}

@Component({
  selector: 'growth-absence-time',
  templateUrl: './absence-time.component.html',
})
export class AbsenceTimeComponent implements OnInit, OnDestroy {
  @ViewChild('editAbsenceModal') editAbsenceModal: ElementRef;
  @Input() profile: ProfileShowResponse | undefined;
  @Input() inquiryId: number;
  @Input() reloadOffersAndCandidates: boolean;
  @Output() pageChange = new EventEmitter();
  @Output() tabUpdate = new EventEmitter<BaseTabInterface>();
  modalRef: NgbModalRef;
  absences: ProfileAbsenceListItemResponse[] = [];
  summary: ProfileAbsencesTypeSummaryResponse;
  absenceToEdit: ProfileAbsenceListItemResponse;
  buttonSize = BaseButtonSize;
  absenceTypes = BaseAbsenceTypeEnum;
  absencePayloadTypes = BaseAbsencePayloadTypeEnum;
  tabs = [...AbsenceTabsConst];
  activeTab = this.tabs[0];
  totalAbsencesNumber: number;
  page = BaseAbsencePagination.page;
  size = BaseAbsencePagination.size;
  itemsCount = 0;
  private destroy$ = new Subject();

  constructor(private store: Store<BaseCoreState>, private modalService: BaseModalService) {}

  ngOnInit() {
    this.loadSummary();
    this.loadAbsences();

    this.store
      .select(baseCandidatesSelectors.getAbsenceSummary)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map((state) => state.data as ProfileAbsencesTypeSummaryResponse)
      )
      .subscribe((summary) => {
        this.totalAbsencesNumber = Object.values(summary).reduce(
          (sum: number, value: number) => sum + value,
          0
        ) as number;

        this.summary = summary;
        this.tabs = this.tabs.map((tab) => ({
          ...tab,
          data: tab.data?.badge
            ? {
                badge: summary[tab.id as keyof ProfileAbsencesTypeSummaryResponse].toString(),
              }
            : undefined,
        }));
      });

    this.store
      .select(baseCandidatesSelectors.getAbsences)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map(
          (state) =>
            state.data as {
              data: ProfileAbsenceListItemResponse[];
              message: string;
              meta: MetaPagination;
            }
        )
      )
      .subscribe((absences) => {
        this.absences = absences.data;
        this.itemsCount = absences.meta?.page.itemsCount as number;
        this.page = absences.meta?.page.current;
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  changePage(event: any) {
    this.page = event;
    this.loadAbsences();
  }

  selectMenuItem(item: BaseTabInterface) {
    this.tabUpdate.emit(item);
    this.page = 1;
    this.activeTab = item;
    this.loadAbsences();
  }

  openEditModal(absence: ProfileAbsenceListItemResponse) {
    this.modalRef = this.modalService.open(
      {
        headText: 'work.absences.edit',
        headerSize: BaseModalHeaderSizes.lg,
        bodyTemplate: this.editAbsenceModal,
        hideFooter: true,
      },
      {
        scrollable: false,
        backdrop: 'static',
        size: 'lg',
      }
    );
    this.modalRef.result.then(noop, noop);
    this.absenceToEdit = absence;
  }

  deleteAbsence(absence: ProfileAbsenceListItemResponse) {
    const deleteModal = this.modalService.open({
      headText: 'work.absences.delete',
      bodyText: 'work.absences.deleteConfirmation',
      okText: 'action.okRemove',
    });

    deleteModal.result.then(() => {
      if (!this.activeTab.id) {
        return;
      }
      const activeTabId = this.activeTab.id?.toString() as BaseAbsenceTypeEnum;

      this.store.dispatch(
        baseCandidateActions.deleteAbsenceAction({
          payload: {
            profile: this.profile?.id as number,
            absence: absence.id,
            inquiry: this.reloadOffersAndCandidates ? this.inquiryId : undefined,
            absenceTab: this.activeTab.id !== this.absenceTypes.all ? this.absencePayloadTypes[activeTabId] : undefined,
          },
        })
      );
    }, noop);
  }

  closeModal() {
    this.modalRef.close();
  }

  private loadSummary() {
    if (!this.profile?.id) {
      return;
    }
    this.store.dispatch(baseCandidateActions.loadAbsenceSummaryAction({ payload: { profile: this.profile.id } }));
  }

  private loadAbsences() {
    if (!this.profile?.id) {
      return;
    }

    const activeTabId = this.activeTab.id?.toString() as BaseAbsenceTypeEnum;
    this.store.dispatch(
      baseCandidateActions.loadAbsencesAction({
        payload: {
          profile: this.profile.id,
          page: this.page,
          type: this.activeTab.id !== this.absenceTypes.all ? this.absencePayloadTypes[activeTabId] : undefined,
          size: this.size,
        },
      })
    );
  }
}
